/**
 * DataTable.tsx — Sortable, cursor-paginated data table
 *
 * Purpose:
 *   Renders a generic tabular data grid for admin list views. Supports
 *   column header click-to-sort, row rendering via a render function,
 *   and a 'Load more' button when a pagination cursor is available.
 *
 * Public API:
 *   - DataTable<T>  Generic sortable table with pagination
 *   - Column<T>     Column definition type
 *   - SortState     Current sort column and direction
 *
 * Architecture Context:
 *   Acceptance Criterion 4: renders rows, supports sort by header click,
 *   shows 'Load more' when cursor prop is non-null (AT-50.01).
 *
 * @module packages/billing-admin/src/components/DataTable
 * @since 2026-05-31 (AT-50.01 — Scaffold Admin Console UI Foundation)
 */
import React from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Sort direction.
 *
 * @since 2026-05-31 (AT-50.01)
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Current sort state.
 *
 * @since 2026-05-31 (AT-50.01)
 */
export interface SortState {
  /** Key of the column currently sorted. */
  column: string;
  /** Direction of the sort. */
  direction: SortDirection;
}

/**
 * Column definition for DataTable.
 *
 * @template T - Row data type
 * @since 2026-05-31 (AT-50.01)
 */
export interface Column<T> {
  /** Unique key for this column (used for sort state). */
  key: string;
  /** Column header label — accepts a string or React element (e.g. a checkbox). */
  header: React.ReactNode;
  /** Renders the cell value for a given row. */
  render: (row: T) => React.ReactNode;
  /** Whether this column is sortable (default: true). */
  sortable?: boolean;
}

interface DataTableProps<T> {
  /** Column definitions. */
  columns: Column<T>[];
  /** Data rows to render. */
  rows: T[];
  /** Unique key extractor for each row. */
  rowKey: (row: T) => string;
  /** Current sort state. */
  sort?: SortState;
  /** Called when the user clicks a sortable column header. */
  onSort?: (column: string, direction: SortDirection) => void;
  /** Cursor for the next page — null when no more pages. */
  cursor?: string | null;
  /** Called when the user clicks 'Load more'. */
  onLoadMore?: () => void;
  /** Whether a load-more request is in flight. */
  loadingMore?: boolean;
  /** Whether the initial data fetch is loading. */
  loading?: boolean;
  /** Empty state message shown when rows is empty and loading is false. */
  emptyMessage?: string;
  /** Enable built-in checkbox multi-select. */
  selectable?: boolean;
  /** Controlled set of selected row keys. Required when selectable=true. */
  selectedKeys?: Set<string>;
  /** Called when selection changes. */
  onSelectionChange?: (keys: Set<string>) => void;
  /** Called when a row is clicked (for opening detail panels). */
  onRowClick?: (row: T) => void;
}

// ---------------------------------------------------------------------------
// DataTable
// ---------------------------------------------------------------------------

export default function DataTable<T>({
  columns,
  rows,
  rowKey,
  sort,
  onSort,
  cursor = null,
  onLoadMore,
  loadingMore = false,
  loading = false,
  emptyMessage = 'No data.',
  selectable = false,
  selectedKeys,
  onSelectionChange,
  onRowClick,
}: DataTableProps<T>): React.JSX.Element {
  const selected = selectedKeys ?? new Set<string>();

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(rowKey(r)));
  const someSelected = rows.some((r) => selected.has(rowKey(r)));

  function toggleAll(): void {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (allSelected) {
      rows.forEach((r) => next.delete(rowKey(r)));
    } else {
      rows.forEach((r) => next.add(rowKey(r)));
    }
    onSelectionChange(next);
  }

  function toggleOne(key: string): void {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    onSelectionChange(next);
  }

  function handleHeaderClick(columnKey: string): void {
    if (!onSort) return;
    if (sort?.column === columnKey) {
      onSort(columnKey, sort.direction === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(columnKey, 'asc');
    }
  }

  const totalCols = columns.length + (selectable ? 1 : 0);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_70px_-40px_rgba(15,23,42,0.55)]">
      <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50/80">
          <tr>
            {selectable && (
              <th scope="col" className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={toggleAll}
                  className="h-4 w-4 rounded border-slate-300 accent-cyan-500 cursor-pointer"
                  aria-label="Select all"
                />
              </th>
            )}
            {columns.map((col) => {
              const isSorted = sort?.column === col.key;
              const isSortable = col.sortable !== false;
              return (
                <th
                  key={col.key}
                  scope="col"
                  onClick={isSortable ? () => handleHeaderClick(col.key) : undefined}
                  className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 ${
                    isSortable ? 'cursor-pointer select-none hover:text-slate-700' : ''
                  }`}
                >
                  <span className="flex items-center gap-1">
                    {col.header}
                    {isSorted && (
                      <span>{sort.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {loading ? (
            <tr>
              <td colSpan={totalCols} className="px-4 py-10 text-center text-slate-400">Loading…</td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={totalCols} className="px-4 py-10 text-center text-slate-400">{emptyMessage}</td>
            </tr>
          ) : (
            rows.map((row) => {
              const key = rowKey(row);
              const isSelected = selected.has(key);
              return (
                <tr key={key} className={`transition hover:bg-cyan-50/40 ${isSelected ? 'bg-cyan-50/60' : ''} ${onRowClick ? 'cursor-pointer' : ''}`} onClick={() => onRowClick?.(row)}>
                  {selectable && (
                    <td className="w-10 px-4 py-3.5">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOne(key)}
                        className="h-4 w-4 rounded border-slate-300 accent-cyan-500 cursor-pointer"
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className="px-4 py-3.5 text-slate-800">
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      </div>

      {cursor !== null && (
        <div className="border-t border-slate-200 bg-slate-50/70 px-4 py-4">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

// End of DataTable.tsx
